/**
 * The Reports export.
 *
 * Replaces a single "Copy for invoice" button, which produced one fixed shape
 * and gave no way to say what a particular reader needed.
 *
 * It produces an invoice ADDENDUM, never an invoice: an external program owns
 * invoicing. The addendum carries what an invoice cannot — every entry with its
 * date and description, and the retainer position — and deliberately carries no
 * terms, no remit-to and no total due, because those would then exist in two
 * places and could disagree.
 */

import { useMemo, useState } from "react";

import { card } from "@/components/atoms";
import { resolveClient } from "@/hooks/useTime";
import {
  breakdownText, budgetStatus, clientNameOf, groupBy, hours, money, periodBounds, projectName,
  retainerHistory, totalsOf, type Period,
} from "@/lib/timeRollup";
import type { Client, Project, TimeEntry } from "@/lib/types";
import { projectRate } from "@/lib/billing";

type Preset = "client" | "addendum" | "internal" | "custom";

const PRESETS: [Preset, string][] = [
  ["client", "Client report"],
  ["addendum", "Invoice addendum"],
  ["internal", "Internal review"],
  ["custom", "Custom"],
];

/** The ten sections, in the order they appear in the output. */
const SECTIONS = [
  { key: "summary", label: "Headline totals" },
  { key: "byProject", label: "By project" },
  { key: "byClient", label: "By client" },
  { key: "byCategory", label: "Not project-specific" },
  { key: "entries", label: "Every entry, dated" },
  { key: "budget", label: "Budget position" },
  { key: "retainer", label: "Retainer history" },
  { key: "rate", label: "Rate and billable hours" },
  { key: "billTo", label: "Bill-to and contract reference" },
  { key: "notes", label: "Client notes verbatim" },
] as const;

type SectionKey = (typeof SECTIONS)[number]["key"];

const PRESET_SECTIONS: Record<Preset, SectionKey[]> = {
  client: ["summary", "byProject", "entries", "budget"],
  addendum: ["summary", "byProject", "entries", "rate", "billTo", "retainer", "notes"],
  internal: ["summary", "byProject", "byClient", "byCategory", "budget", "retainer"],
  custom: ["summary", "byProject"],
};

export function ExportPanel({ entries, projects, clients, period, offset, onClose }: {
  entries: TimeEntry[];
  projects: Project[];
  clients: Client[];
  period: Period;
  offset: number;
  onClose: () => void;
}) {
  const bounds = periodBounds(period, offset);
  const [preset, setPreset] = useState<Preset>("client");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [on, setOn] = useState<Set<SectionKey>>(new Set(PRESET_SECTIONS.client));
  const [copied, setCopied] = useState(false);

  const scoped = useMemo(() => entries.filter((e) => {
    if (clientId && e.client !== clientId) return false;
    if (projectId && e.project !== projectId) return false;
    return true;
  }), [entries, clientId, projectId]);

  const client = clients.find((c) => c.id === clientId);
  const project = projects.find((p) => p.id === projectId);
  const totals = totalsOf(scoped, projects);

  /**
   * Why a section has nothing to say. A section that would be empty is unticked
   * with the reason beside it, rather than exporting a blank heading.
   */
  const unavailable = useMemo(() => {
    const out: Partial<Record<SectionKey, string>> = {};
    const clientsInScope = new Set(scoped.map((e) => e.client).filter(Boolean));
    if (clientsInScope.size <= 1) out.byClient = "one client in scope";
    if (!scoped.some((e) => !e.project)) out.byCategory = "no ad-hoc time in scope";
    if (scoped.length === 0) out.entries = "nothing logged in this period";
    if (!project) out.budget = "pick a project";
    else if (!budgetStatus(project, entries)) out.budget = "no budget set";
    if (!project || project.billingType !== "retainer") out.retainer = "not a retainer";
    if (!project && !client) out.rate = "pick a project or client";
    if (!client) out.billTo = "pick a client";
    if (!client?.notes?.trim()) out.notes = "no notes on this client";
    return out;
  }, [scoped, project, client, entries]);

  function applyPreset(next: Preset) {
    setPreset(next);
    // Presets are starting points — every checkbox stays live afterwards.
    setOn(new Set(PRESET_SECTIONS[next].filter((k) => !unavailable[k])));
  }

  function toggle(k: SectionKey) {
    setPreset("custom");
    setOn((v) => {
      const next = new Set(v);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  }

  const chosen = SECTIONS.filter((s) => on.has(s.key) && !unavailable[s.key]);

  function build(): string {
    const L: string[] = [];
    const title = preset === "addendum" ? "Invoice addendum" : preset === "internal" ? "Internal review" : "Time report";
    L.push(title, "");

    if (preset === "addendum") {
      // Prefilled from the records rather than merely ticked.
      L.push(`Reference: ${(project?.contract || client?.name || "—")}-${bounds.label.replace(/\s+/g, "")}`);
      L.push(`Prepared: ${new Date().toLocaleDateString()}`);
      L.push(`Period: ${bounds.label}`);
      if (client) L.push(`Bill to: ${client.name}`);
      if (project?.contract) L.push(`Contract: ${project.contract}`);
      L.push("");
      L.push("This addendum details hours behind the invoice. It carries no totals due, terms or remittance details — the invoice owns those.");
      L.push("");
    } else {
      L.push(`Period: ${bounds.label}`);
      if (client) L.push(`Client: ${client.name}`);
      if (project) L.push(`Project: ${project.name}`);
      L.push("");
    }

    for (const s of chosen) {
      if (s.key === "summary") {
        L.push("— Totals —");
        L.push(`${hours(totals.seconds)} h logged${breakdownText(totals, hours) ? ` (${breakdownText(totals, hours)})` : ""}`);
        if (totals.amount > 0) L.push(`Amount: ${money(totals.amount, totals.currency)}`);
        L.push("");
      }
      if (s.key === "byProject") {
        L.push("— By project —");
        for (const [id, rows] of groupBy(scoped.filter((e) => e.project), (e) => e.project!)) {
          const t = totalsOf(rows, projects);
          L.push(`${projectName(projects, id)}: ${hours(t.seconds)} h${t.amount > 0 ? ` — ${money(t.amount, t.currency)}` : ""}`);
        }
        L.push("");
      }
      if (s.key === "byClient") {
        L.push("— By client —");
        for (const [id, rows] of groupBy(scoped.filter((e) => e.client), (e) => e.client!)) {
          const t = totalsOf(rows, projects);
          L.push(`${clientNameOf(clients, id) ?? "—"}: ${hours(t.seconds)} h${t.amount > 0 ? ` — ${money(t.amount, t.currency)}` : ""}`);
        }
        L.push("");
      }
      if (s.key === "byCategory") {
        L.push("— Not project-specific —");
        for (const [label, rows] of groupBy(scoped.filter((e) => !e.project), (e) => e.category || "Uncategorised")) {
          L.push(`${label}: ${hours(totalsOf(rows, projects).seconds)} h`);
        }
        L.push("");
      }
      if (s.key === "entries") {
        // The thing an invoice cannot carry.
        L.push("— Entries —");
        for (const e of [...scoped].sort((a, b) => a.start.localeCompare(b.start))) {
          L.push(`${e.start.slice(0, 10)}  ${hours(e.durationSeconds).padStart(6)} h  ${e.note || projectName(projects, e.project) || "Time"}`);
        }
        L.push("");
      }
      if (s.key === "budget" && project) {
        const b = budgetStatus(project, entries);
        if (b) {
          L.push("— Budget —");
          L.push(`${hours(b.usedSeconds)} h of ${b.budgetHours} h (${b.window})`);
          L.push("");
        }
      }
      if (s.key === "retainer" && project) {
        // A month over allowance is what a client queries.
        const months = retainerHistory(project, entries).filter((m) => !m.beforeStart);
        if (months.length) {
          L.push("— Retainer position —");
          for (const m of months) L.push(`${m.label}: ${m.hours.toFixed(2)} h of ${project.retainerHours}`);
          L.push("");
        }
      }
      if (s.key === "rate") {
        const rate = projectRate(project) ?? resolveClient(project ?? ({} as Project), clients)?.defaultRate ?? client?.defaultRate ?? null;
        L.push("— Rate —");
        L.push(`${rate !== null ? `${money(rate, totals.currency)}/h` : "no rate on record"} · ${hours(totals.billableSeconds)} h billable`);
        L.push("");
      }
      if (s.key === "billTo" && client) {
        L.push("— Bill to —");
        L.push(client.name);
        if (project?.contract) L.push(`Contract: ${project.contract}`);
        L.push("");
      }
      if (s.key === "notes" && client?.notes) {
        // Quoted verbatim: payment terms live in the note, not in new columns.
        L.push("— Client notes —");
        L.push(client.notes);
        L.push("");
      }
    }
    return L.join("\n").trimEnd();
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(build());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  }

  const text = build();
  const pageEstimate = Math.max(1, Math.ceil(text.split("\n").length / 48));

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 60, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "50px 20px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: 640, maxWidth: "100%", padding: "22px 24px", background: "var(--raised)", border: "1px solid var(--overlayLine)", boxShadow: "var(--shadowOverlay)" }}>
        <h2 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 500 }}>Export</h2>

        <div style={{ display: "flex", padding: 3, background: "var(--tint)", borderRadius: 999, marginBottom: 14 }}>
          {PRESETS.map(([k, label]) => (
            <button key={k} onClick={() => applyPreset(k)} style={{
              flex: 1, padding: "6px 10px", borderRadius: 999, fontSize: 12, cursor: "pointer", border: "none",
              background: preset === k ? "var(--surface)" : "transparent",
              color: preset === k ? "var(--accent)" : "var(--ink3)",
              fontWeight: preset === k ? "var(--fw-title)" : "var(--fw-medium)",
            }}>{label}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          <label style={lbl}>
            Client
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={fld}>
              <option value="">All clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label style={lbl}>
            Project
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={fld}>
              <option value="">All projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        </div>

        {/* Period is not editable here: it inherits the page's selection, and
            two places to set it is how the two end up disagreeing. */}
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14 }}>
          Period: <strong style={{ color: "var(--ink2)" }}>{bounds.label}</strong> — taken from the page above. Change it there.
        </div>

        <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 6 }}>Include</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", marginBottom: 14 }}>
          {SECTIONS.map((s) => {
            const why = unavailable[s.key];
            return (
              <label key={s.key} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: why ? "var(--muted3)" : "var(--ink2)" }}>
                <input type="checkbox" checked={on.has(s.key) && !why} disabled={Boolean(why)} onChange={() => toggle(s.key)} />
                {s.label}
                {why && <span style={{ fontSize: 11, color: "var(--muted3)" }}>— {why}</span>}
              </label>
            );
          })}
        </div>

        <pre style={{
          margin: 0, maxHeight: 180, overflow: "auto", background: "var(--tint)", borderRadius: 8,
          padding: 12, fontSize: 11.5, fontFamily: "var(--font-mono)", color: "var(--ink3)", whiteSpace: "pre-wrap",
        }}>{text}</pre>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
          <span style={{ fontSize: 11.5, color: "var(--muted2)" }}>
            {chosen.length} section{chosen.length === 1 ? "" : "s"} · ~{pageEstimate} page{pageEstimate === 1 ? "" : "s"}
          </span>
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={onClose} style={ghost}>Cancel</button>
            <button onClick={() => void copy()} style={primary}>{copied ? "Copied" : "Copy export"}</button>
          </span>
        </div>
      </div>
    </div>
  );
}

const lbl: React.CSSProperties = { display: "block", fontSize: 11.5, color: "var(--muted)" };
const fld: React.CSSProperties = {
  width: "100%", marginTop: 4, padding: "7px 9px", borderRadius: 8, fontSize: 12.5,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
const primary: React.CSSProperties = {
  padding: "8px 16px", borderRadius: 999, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, cursor: "pointer",
};
const ghost: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
};
